import React, { useEffect, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import DatepickerMultipleField from '@components/common/form/DatepickerMultipleField';
import DropdownField from '@components/common/form/DropdownField';
import FieldSet from '@components/common/form/FieldSet';
import TimePickerField from '@components/common/form/TimePickerField';
import { TIME_FORMAT_DISPLAY } from '@constants';
import { daysOfWeekTimeWork as daysOfWeekTimeWorks, orderMethods as orderMethod } from '@constants/masterData';
import useTranslate from '@hooks/useTranslate';
import { NumericField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { showErrorMessage } from '@services/notifyService';
import { getDefaultTimeWork } from '@utils';
import { getDisabledHours, getDisabledMinutes } from '@utils/dayHelper';
import { Button, Col, Flex, Form, Row, Space, Switch } from 'antd';
import moment from 'moment';

import './index.scss';

const isMidnight = (value) => !!value && value.hour() === 0 && value.minute() === 0;

const getDisabledMinutesForTo = (hour, fromValue) => {
    const baseDisabled = getDisabledMinutes(hour, fromValue) || [];

    if (
        fromValue &&
        fromValue.hour() === hour &&
        !isMidnight(fromValue)
    ) {
        return [...new Set([...baseDisabled, fromValue.minute()])];
    }

    return baseDisabled;
};

const StoreSiteSettingForm = ({
    formId,
    settingObj,
    onSubmit,
    setIsChangedFormValues,
    isChangedFormValues,
    loadingUpdate,
}) => {
    const translate = useTranslate();
    const [form] = Form.useForm();
    const enableTimeWork = Form.useWatch('enable_time_work', form);
    const [maxDistances, setMaxDistances] = useState([]);

    const orderMethods = translate.formatKeys(orderMethod, ['label']);
    const daysOfWeekTimeWork = translate.formatKeys(daysOfWeekTimeWorks, ['label']);
    const [forceRenderKeys, setForceRenderKeys] = useState({});

    useEffect(() => {
        const initialMaxDistances =
            settingObj.deliver_setting && settingObj.deliver_setting.distance
                ? settingObj.deliver_setting.distance.map((distance) => distance.km)
                : [];
        setMaxDistances(initialMaxDistances);

        form.setFieldsValue({
            ...settingObj,
        });
    }, [settingObj, form]);

    const onValuesChange = () => {
        setIsChangedFormValues(true);
    };

    const checkDistanceMinKm = (index) => {
        let minValue = 0;
        if (index > 0) {
            const distances = form.getFieldValue(['deliver_setting', 'distance']);
            const distance = distances[index - 1];
            if (distance && distance.km) {
                minValue = distance.km + 1;
            }
        }
        return minValue;
    };

    const checkAddDistance = (addCallback) => {
        const distances = form.getFieldValue(['deliver_setting', 'distance']);
        if (distances && distances.length > 0 && !distances[distances.length - 1]) {
            showErrorMessage('orderSettings:message.requireInputDistanceFirst', translate);
        } else {
            addCallback();
        }
    };

    const calculateMaxDistance = () => {
        const distances = form.getFieldValue(['deliver_setting', 'distance']);
        if (distances && distances.length > 0) {
            const newMaxDistances = distances.map((distance) => distance?.km ?? 1000);
            setMaxDistances(newMaxDistances);
        }
    };

    const onSelectTimeWorkTabletRandom = (fieldName, value) => {
        const time_work = form.getFieldValue('time_work');
        const [dayKey, dayIndexKey, frameKey] = fieldName;
        if (frameKey === 'from') {
            const to = time_work[dayKey][dayIndexKey].to;
            // if (to && to.format(TIME_FORMAT_DISPLAY) < value?.format(TIME_FORMAT_DISPLAY)) {
            //     time_work[dayKey][dayIndexKey].to = null;
            // }
            const toIsInvalid =
                to &&
                value &&
                (
                    to.isBefore(value) ||
                    (to.isSame(value) && !isMidnight(value))
                );

            if (toIsInvalid) {
                time_work[dayKey][dayIndexKey].to = null;
            }
        } else if (frameKey === 'to') {
            const from = time_work[dayKey][dayIndexKey].from;
            // if (from && value?.format(TIME_FORMAT_DISPLAY) < from.format(TIME_FORMAT_DISPLAY)) {
            //     value = from;
            // }
            if (from && value) {
                if (value.isBefore(from)) {
                    value = from;
                } else if (value.isSame(from) && !isMidnight(from)) {
                    return;
                }
            }
        }
        time_work[dayKey][dayIndexKey][frameKey] = value;
        form.setFieldsValue({ time_work });
        onValuesChange();
    };

    const handleApplyAll = (e) => {
        e.preventDefault();
        const time_work = form.getFieldValue('time_work');
        const { monday = [] } = time_work;

        for (let { value } of daysOfWeekTimeWork) {
            time_work[value] = monday.map((frame) => ({
                from: moment(frame.from),
                to: moment(frame.to),
            }));
        }

        form.setFieldsValue({ time_work });
        onValuesChange();
    };

    const checkCanApplyAll = () => {
        const time_work = form.getFieldValue('time_work');
        const { monday } = time_work || {};
        if (!monday) return false;
        return monday.every((frame) => !!frame.from && !!frame.to);
    };

    const renderDistance = () => {
        return (
            <>
                <Row gutter={16}>
                    <Col span={8}>
                        <TextField
                            name={['deliver_setting', 'region_google']}
                            label={translate.formatMessage(commonMessage.regionGoogle)}
                        />
                    </Col>
                    <Col span={8}>
                        <TextField
                            name={['deliver_setting', 'map_key_direction']}
                            label={translate.formatMessage(commonMessage.mapKey)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={18}>
                        <Form.List name={['deliver_setting', 'distance']}>
                            {(fields, { add, remove }) => (
                                <div className="no-margin-form-item">
                                    {fields.map((field, index) => (
                                        <Space
                                            key={field.key}
                                            style={{ display: 'flex', marginBottom: 8 }}
                                            align="start"
                                        >
                                            <NumericField
                                                formatter={(v) => v}
                                                parser={(v) => v}
                                                name={[field.name, 'km']}
                                                placeholder={translate.formatMessage(commonMessage.km)}
                                                width="100%"
                                                min={checkDistanceMinKm(index)}
                                                max={
                                                    maxDistances.length > index + 1 ? maxDistances[index + 1] - 1 : 1000
                                                }
                                                onBlur={calculateMaxDistance}
                                                required
                                            />
                                            <NumericField
                                                formatter={(v) => v}
                                                parser={(v) => v}
                                                name={[field.name, 'min_money']}
                                                placeholder={translate.formatMessage(commonMessage.minMoney)}
                                                width="100%"
                                                min={0}
                                                required
                                            />
                                            <NumericField
                                                formatter={(v) => v}
                                                parser={(v) => v}
                                                name={[field.name, 'ship_money']}
                                                placeholder={translate.formatMessage(commonMessage.shipMoney)}
                                                width="100%"
                                                min={0}
                                                required
                                            />
                                            <MinusCircleOutlined
                                                onClick={() => {
                                                    remove(field.name);
                                                    calculateMaxDistance();
                                                }}
                                            />
                                        </Space>
                                    ))}
                                    <Form.Item style={{ width: '170px' }}>
                                        <Button type="dashed" onClick={() => checkAddDistance(add)}>
                                            <PlusOutlined /> {translate.formatMessage(commonMessage.addDistance)}
                                        </Button>
                                    </Form.Item>
                                </div>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </>
        );
    };

    const renderPrinters = () => {
        return (
            <Row gutter={16}>
                <Col span={18}>
                    <Form.List name="printers">
                        {(fields, { add, remove }) => (
                            <div className="no-margin-form-item">
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                        <TextField
                                            name={[field.name, 'name']}
                                            placeholder={translate.formatMessage(commonMessage.name)}
                                        />
                                        <TextField
                                            name={[field.name, 'code']}
                                            placeholder={translate.formatMessage(commonMessage.code)}
                                        />
                                        <TextField
                                            name={[field.name, 'ip']}
                                            placeholder={translate.formatMessage(commonMessage.ip)}
                                        />
                                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                                    </Space>
                                ))}
                                <Form.Item style={{ width: '170px' }}>
                                    <Button type="dashed" onClick={() => add()} block>
                                        <PlusOutlined /> {translate.formatMessage(commonMessage.addPrinter)}
                                    </Button>
                                </Form.Item>
                            </div>
                        )}
                    </Form.List>
                </Col>
            </Row>
        );
    };

    const renderTabletOrder = () => {
        return (
            <>
                <Row gutter={16}>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_min_time_complete_online_pickup"
                            label={translate.formatMessage(commonMessage.onlineAppointmentPickUp)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_step_online_pickup"
                            label={translate.formatMessage(commonMessage.onlineAppointmentStepPickUp)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_min_time_complete_online"
                            label={translate.formatMessage(commonMessage.onlineAppointment)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_step_online"
                            label={translate.formatMessage(commonMessage.onlineAppointmentStep)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_min_time_complete_qrcode"
                            label={translate.formatMessage(commonMessage.restaurantAppointment)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_step_qrcode"
                            label={translate.formatMessage(commonMessage.restaurantAppointmentStep)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_wait_hide_archive"
                            label={translate.formatMessage(commonMessage.expireTime)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_change_from_order_to_processing"
                            label={translate.formatMessage(commonMessage.scheduleTimeProcessing)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_wait_move_to_archive"
                            label={translate.formatMessage(commonMessage.scheduleTimeArchive)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_wait_hide_none_payment"
                            label={translate.formatMessage(commonMessage.timeHideNonePayment)}
                            width="100%"
                        />
                    </Col>
                    <Col span={8}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            name="tablet_time_finish_one_order"
                            label={translate.formatMessage(commonMessage.timeFinishOneOrder)}
                            width="100%"
                        />
                    </Col>
                </Row>
            </>
        );
    };

    const renderTimeWork = () => {
        const isEnabled = enableTimeWork;
        return (
            <table className="happy-hours-table">
                <thead>
                    <tr>
                        <th width="14%">{translate.formatMessage(commonMessage.dayOfWeek)}</th>
                        <th>{translate.formatMessage(commonMessage.timeFrame)}</th>
                    </tr>
                </thead>
                <tbody>
                    {daysOfWeekTimeWork.map((day, dayIndex) => (
                        <tr key={day.value}>
                            <td>{day.label}</td>
                            <td style={{ padding: '10px' }}>
                                <Form.List name={['time_work', day.value]}>
                                    {(fields) => (
                                        <div className="no-margin-form-item">
                                            <Space className="box-flex" size={24}>
                                                {fields.map((field, index) => (
                                                    <div key={field.key}>
                                                        <div className="frame-label">
                                                            {translate.formatMessage(commonMessage.frameTime)}{' '}
                                                            {index + 1}
                                                        </div>
                                                        <Space className="box-flex">
                                                            <TimePickerField
                                                                disabled={!isEnabled}
                                                                size="small"
                                                                fieldName={[field.name, 'from']}
                                                                onChange={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [day.value, field.name, 'from'],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(
                                                                    commonMessage.from,
                                                                )}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireFrom,
                                                                )}
                                                                validateTrigger={['onChange', 'onBlur']}
                                                                onClick={onValuesChange}
                                                            />
                                                            <TimePickerField
                                                                disabled={!isEnabled}
                                                                size="small"
                                                                fieldName={[field.name, 'to']}
                                                                onChange={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [day.value, field.name, 'to'],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(commonMessage.to)}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireTo,
                                                                )}
                                                                validateTrigger={['onChange', 'onBlur']}
                                                                disabledHours={() => {
                                                                    const timeWork = form.getFieldValue('time_work');
                                                                    const from = timeWork[day.value][field.name].from;
                                                                    return getDisabledHours(from);
                                                                }}
                                                                disabledMinutes={(hour) => {
                                                                    const timeWork = form.getFieldValue('time_work');
                                                                    const from = timeWork[day.value][field.name].from;
                                                                    // return getDisabledMinutes(hour, from);
                                                                    return getDisabledMinutesForTo(hour, from);
                                                                }}
                                                                onClick={onValuesChange}
                                                            />
                                                        </Space>
                                                    </div>
                                                ))}
                                                {!dayIndex && (
                                                    <div className="wrap-btn-apply-all">
                                                        <Button
                                                            disabled={!isEnabled || !checkCanApplyAll()}
                                                            type="primary"
                                                            size="small"
                                                            onClick={handleApplyAll}
                                                        >
                                                            {translate.formatMessage(commonMessage.applyAll)}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Space>
                                        </div>
                                    )}
                                </Form.List>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <Form
            id={formId}
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={settingObj}
            onValuesChange={onValuesChange}
        >
            <Row gutter={16}>
                <Col span={8}>
                    <DropdownField
                        mode="multiple"
                        fieldName="paymentDeliver"
                        label={translate.formatMessage(commonMessage.paymentMethod)}
                        options={orderMethods}
                    />
                </Col>
            </Row>
            <FieldSet className="custom-fieldset" title={translate.formatMessage(commonMessage.message)}>
                <Row gutter={16}>
                    <Col span={8}>
                        <TextField
                            rows={4}
                            type="textarea"
                            name={['message', 'accepted']}
                            label={translate.formatMessage(commonMessage.accepted)}
                        />
                    </Col>
                    <Col span={8}>
                        <TextField
                            rows={4}
                            type="textarea"
                            name={['message', 'processing']}
                            label={translate.formatMessage(commonMessage.processing)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <TextField
                            rows={4}
                            className="mb-0"
                            type="textarea"
                            name={['message', 'pickup_done']}
                            label={translate.formatMessage(commonMessage.pickupDone)}
                        />
                    </Col>
                    <Col span={8}>
                        <TextField
                            rows={4}
                            className="mb-0"
                            type="textarea"
                            name={['message', 'deliver_done']}
                            label={translate.formatMessage(commonMessage.deliverDone)}
                        />
                    </Col>
                    <Col span={8}>
                        <TextField
                            rows={4}
                            className="mb-0"
                            type="textarea"
                            name={['message', 'qrlive_done']}
                            label={translate.formatMessage(commonMessage.qRLiveDone)}
                        />
                    </Col>
                </Row>
            </FieldSet>
            <FieldSet className="custom-fieldset mt-4" title={translate.formatMessage(commonMessage.distance)}>
                {renderDistance()}
            </FieldSet>
            {/* <FieldSet className="custom-fieldset mt-4" title={t('title.printers')}>
                {renderPrinters()}
            </FieldSet> */}
            <FieldSet className="custom-fieldset mt-4" title={translate.formatMessage(commonMessage.timeSchedule)}>
                {renderTabletOrder()}
            </FieldSet>
            <FieldSet className="custom-fieldset mt-4" title={
                <Flex align="center" gap={16}>
                    <span>{translate.formatMessage(commonMessage.timeWork)}</span>
                    <Form.Item
                        name={['enable_time_work']}
                        valuePropName="checked"
                        noStyle
                    >
                        <Switch
                            onChange={(checked) => {
                                form.setFieldsValue({
                                    time_work: checked ? getDefaultTimeWork() : settingObj.general_time_work,
                                    enable_time_work: checked,
                                });
                                onValuesChange();
                            }}
                        />
                    </Form.Item>
                </Flex>
            }>
                {renderTimeWork()}
                <Row gutter={16} style={{ marginTop: '16px' }}>
                    <Col span={10}>
                        <DatepickerMultipleField
                            disabledSelect={!enableTimeWork}
                            disabled={!enableTimeWork}
                            fieldName={['time_work', 'dayOffs']}
                            label={translate.formatMessage(commonMessage.dayOffs)}
                        />
                    </Col>
                </Row>
            </FieldSet>

            <Flex justify="end">
                <Button
                    style={{ marginLeft: '10rem', marginTop: 12 }}
                    key="submit"
                    htmlType="submit"
                    type="primary"
                    disabled={!isChangedFormValues}
                    loading={loadingUpdate}
                >
                    <div>
                        <SaveOutlined style={{ marginRight: '6px' }} />
                        {translate.formatMessage(commonMessage.update)}
                    </div>
                </Button>
            </Flex>
        </Form>
    );
};

export default StoreSiteSettingForm;
